import { v4 as uuidv4 } from 'uuid'
import Meta, { NOTE_FORMAT } from './Meta'
import store from './store/index'
import { INoteModule } from '@/contracts/modules/note/INoteModule'
import { IModuleMeta } from '@/contracts/core/module/interface/IModuleMeta'
import { INote } from '@/contracts/modules/note/entities/INote'
import { INotePreview } from '@/contracts/modules/note/entities/INotePreview'
import { IDocument, IDocumentNode } from '@/contracts/modules/note/entities/IDocument'
import { IStore } from '@/contracts/modules/note/stores/IStore'
import { EnumNoteSet } from '@/contracts/modules/note/enum/EnumNoteSet'
import { EnumNoteEvent, EnumTabsEvent } from '@/contracts/modules/note/enum/EnumNoteEvent'
import { EnumNoteError } from '@/contracts/modules/note/enum/EnumNoteError'
import { EnumOperationKind } from '@/contracts/system-modules/sync/enum/EnumOperationKind'
import { EnumSystemEvent } from '@/contracts/core/event/enum/EnumSystemEvent'
import { IOperation } from '@/contracts/system-modules/sync/operation/IOperation'
import { IUserKey } from '@/contracts/system-modules/auth/key/IUserKey'
import { EnumUserKeyError } from '@/contracts/system-modules/auth/key/EnumUserKeyError'
import { INoteCipher } from '@/contracts/modules/note/cipher/INoteCipher'
import { IDecryptNote } from '@/contracts/modules/note/use-case/IDecryptNote'
import { EnumNotePart } from '@/contracts/modules/note/enum/EnumNotePart'
import { EnumConsoleType } from '@/contracts/system-modules/console/enum/EnumConsoleType'
import { ApiResponse } from '@/contracts/core/api/entity/ApiResponse'
import { SysConsoleProviderModule } from '@/system-modules/console/ConsoleProvider'
import { sys } from '@/system/SystemProvider'
import { Note } from '@/modules/note/v1/entities/Note'
import { NotePreview } from '@/modules/note/v1/entities/NotePreview'
import { FoldNote } from '@/modules/note/v1/use-case/FoldNote'
import { FoldTabs } from '@/modules/note/v1/use-case/FoldTabs'
import { DecryptNote } from '@/modules/note/v1/use-case/DecryptNote'
import { NoteCipher } from '@/modules/note/v1/cipher/NoteCipher'
import { INoteIndexRow, readNoteIndex, removeNoteIndexRow, writeNoteIndexRow } from '@/modules/note/v1/storage/NoteIndex'
import { INoteFile } from '@/contracts/modules/note/entities/INoteFile'
import { INoteFileRecord } from '@/contracts/modules/note/entities/INoteFileRecord'
import { IUploadEntry } from '@/contracts/modules/note/entities/IUploadEntry'
import { ITransfer } from '@/contracts/modules/note/entities/ITransfer'
import { IFileCipher } from '@/contracts/modules/note/cipher/IFileCipher'
import { EnumTransferKind } from '@/contracts/modules/note/enum/EnumTransferKind'
import { EnumNoteFileError } from '@/contracts/modules/note/enum/EnumNoteFileError'
import { EnumApiError } from '@/contracts/core/api/enum/EnumApiError'
import { IFileSink } from '@/contracts/core/file/IFileSink'
import { FileSaverProviderModule } from '@/core/file/FileSaverProvider'
import { ZipWriter } from '@/core/file/v1/ZipWriter'
import { FileCipher, GCM_TAG_BYTES } from '@/modules/note/v1/cipher/FileCipher'
import { NoteFile } from '@/modules/note/v1/entities/NoteFile'
import { Transfer } from '@/modules/note/v1/entities/Transfer'
import { TransferCancelled, TransferError, TransferQueue } from '@/modules/note/v1/use-case/TransferQueue'
import { ChunkReader } from '@/modules/note/v1/use-case/ChunkReader'
import { CreateUploadRoute } from '@/modules/note/v1/api/v1/files/CreateUpload/CreateUploadRoute'
import { SendChunkRoute } from '@/modules/note/v1/api/v1/files/SendChunk/SendChunkRoute'
import { GetUploadRoute } from '@/modules/note/v1/api/v1/files/GetUpload/GetUploadRoute'
import { CancelUploadRoute } from '@/modules/note/v1/api/v1/files/CancelUpload/CancelUploadRoute'
import { GetFileChunkRoute } from '@/modules/note/v1/api/v1/files/GetFileChunk/GetFileChunkRoute'
import { RemoveFilesRoute } from '@/modules/note/v1/api/v1/files/RemoveFiles/RemoveFilesRoute'
import { CreateUploadParams } from '@/modules/note/v1/api/v1/files/Dto/CreateUploadParams'
import { ChunkParams } from '@/modules/note/v1/api/v1/files/Dto/ChunkParams'
import { UploadParams } from '@/modules/note/v1/api/v1/files/Dto/UploadParams'
import { FileChunkParams } from '@/modules/note/v1/api/v1/files/Dto/FileChunkParams'
import { RemoveFilesParams } from '@/modules/note/v1/api/v1/files/Dto/RemoveFilesParams'

export const IMAGE_LIMIT_BYTES = 2 * 1024 * 1024
export const NOTE_LIMIT_BYTES = 10 * 1024 * 1024
export const UNTITLED = 'Без названия'
/** Название в списке для заметки, чей ключ или содержимое не расшифровались */
export const UNREADABLE_TITLE = 'Не удалось расшифровать'
/** Как часто проверяется, ушла ли очередь на узел: по таймеру модуля sync */
export const PENDING_CHECK_MS = 5000
/** Кусок файла: 8 МиБ открытого текста; на узле — на 16 байт метки больше */
export const CHUNK_BYTES = 8 * 1024 * 1024
/** Паузы повторов, предел повторов куска при скачивании и размер куска загрузки; тесты уменьшают их */
export const transferSettings = { retryBaseMs: 2000, retryMaxMs: 60000, downloadRetries: 5, chunkBytes: CHUNK_BYTES }

/** Сколько кусков в файле; пустой файл — один кусок из одной метки */
function chunkCount(size: number, chunk: number): number {
  return Math.max(1, Math.ceil(size / chunk))
}

/** Ошибка, после которой запрос повторяется: сеть, узел или storage временно не отвечают, порядок кусков разошёлся */
function isRetryable(code: string): boolean {
  return [EnumApiError.NETWORK_ERROR, EnumApiError.BAD_RESPONSE, 'storage_unavailable', 'service_unavailable', 'server_error', 'wrong_offset'].includes(code)
}

function fileError(code: string): EnumNoteFileError {
  switch (code) {
  case 'forbidden': return EnumNoteFileError.FORBIDDEN
  case 'too_large': return EnumNoteFileError.TOO_LARGE
  case 'not_found': return EnumNoteFileError.NOT_ON_NODE
  case 'storage_unavailable': return EnumNoteFileError.STORAGE_UNAVAILABLE
  default: return EnumNoteFileError.NETWORK
  }
}

function compareTime(a: string | null, b: string | null): number {
  const left = a ?? ''
  const right = b ?? ''
  return left < right ? -1 : left > right ? 1 : 0
}

/** Размер картинки в байтах по её data:-URL */
function imageBytes(url: string): number {
  const comma = url.indexOf(',')
  const data = comma === -1 ? url : url.slice(comma + 1)
  return Math.floor((data.length * 3) / 4)
}

class NoteModuleV1 implements INoteModule {
  private static instance: NoteModuleV1
  private store: IStore = store()
  private userUuid = ''
  private starting: Promise<void> | null = null
  private pendingTimer: ReturnType<typeof setInterval> | null = null
  private foldNote = new FoldNote()
  private foldTabs = new FoldTabs()
  private cipher: INoteCipher = new NoteCipher()
  private decryptNote: IDecryptNote = new DecryptNote()
  /** Ключи заметок после unwrap: unwrap делается один раз на заметку */
  private keys = new Map<string, CryptoKey>()
  /** Заметки, ключ или содержимое которых не расшифровались: в списке, но не открываются */
  private unreadable = new Set<string>()
  /** Устаревшие заметки — без ключа в событии created: клиент их не показывает */
  private legacy = new Set<string>()
  // Шифрование асинхронное, а события одной заметки должны уходить в порядке вызовов:
  // запись каждой заметки идёт своей цепочкой обещаний
  private writing = new Map<string, Promise<unknown>>()
  private writesInFlight = 0
  /** Номер последнего обновления заметки: обновление, начатое раньше и закончившееся позже, отбрасывается */
  private refreshing = new Map<string, number>()
  private fileCipher: IFileCipher = new FileCipher()
  private queue = new TransferQueue()
  /** Ключи файлов после unwrap и расшифрованные сведения, по UUID файла */
  private fileKeys = new Map<string, CryptoKey>()
  private fileInfo = new Map<string, INoteFile>()

  static getInstance(): NoteModuleV1 {
    if (!NoteModuleV1.instance) {
      NoteModuleV1.instance = new NoteModuleV1()
    }
    return NoteModuleV1.instance
  }

  health(): boolean {
    return true
  }

  init(): boolean {
    sys().events().on(EnumSystemEvent.LOCKED, () => this.stop())
    sys().events().on(EnumSystemEvent.UNLOCKED, () => { this.start() })
    this.start()
    return true
  }

  destroy(): boolean {
    this.stop()
    return true
  }

  getMeta(): IModuleMeta {
    return new Meta()
  }

  getViewPath(): string {
    return '/note/v1/view/index.vue'
  }

  start(): Promise<void> {
    const userUuid = sys().auth.getUserUuid()
    if (userUuid === '' || !this.hasUserKey()) {
      return Promise.resolve()
    }
    if (this.starting !== null && this.userUuid === userUuid) {
      return this.starting
    }
    this.reset()
    this.userUuid = userUuid
    sys().sync.subscribe(EnumNoteSet.NOTES, (operation) => this.onNoteOperation(operation))
    sys().sync.subscribe(EnumNoteSet.TABS, (operation) => this.onTabsOperation(operation))
    this.pendingTimer = setInterval(() => this.checkPending(), PENDING_CHECK_MS)
    this.starting = this.load(userUuid)
    return this.starting
  }

  hasUserKey(): boolean {
    return sys().auth.getUserKey() !== null
  }

  async create(): Promise<string> {
    await this.start()
    const userKey = this.requireUserKey()
    const uuid = uuidv4()
    const title = this.nextUntitled()
    const noteKey = await this.cipher.createKey()
    const wrapped = await userKey.wrap(noteKey)
    this.keys.set(uuid, await userKey.unwrap(wrapped))
    this.write(EnumNoteSet.NOTES, uuid, EnumNoteEvent.CREATED, { key: wrapped, title: await this.cipher.encrypt(noteKey, EnumNotePart.TITLE, title) })
    this.markPending(uuid)
    this.open(uuid)
    return uuid
  }

  open(uuid: string): void {
    if (this.isHidden(uuid)) {
      return
    }
    const open = this.store.openTabs.includes(uuid) ? this.store.openTabs : [...this.store.openTabs, uuid]
    this.writeTabs(open, uuid)
    this.store.isOpenDialogShown = false
  }

  close(uuid: string): void {
    const open = this.store.openTabs.filter((tab) => tab !== uuid)
    let active = this.store.activeTab
    if (active === uuid) {
      const index = this.store.openTabs.indexOf(uuid)
      active = open[Math.min(index, open.length - 1)] ?? null
    }
    this.writeTabs(open, active)
  }

  activate(uuid: string): void {
    if (this.store.activeTab !== uuid && this.store.openTabs.includes(uuid)) {
      this.writeTabs(this.store.openTabs, uuid)
    }
  }

  rename(uuid: string, title: string): void {
    this.requireUserKey()
    this.markPending(uuid)
    this.writeNote(uuid, EnumNoteEvent.RENAMED, async () => ({ title: await this.encrypt(uuid, EnumNotePart.TITLE, title) }))
  }

  changeContent(uuid: string, content: IDocument): void {
    this.requireUserKey()
    const note = this.supportedNote(uuid)
    this.checkLimits(content)
    if (note.getConflict() !== null) {
      return
    }
    const heads = note.getHeads()
    const base = heads.length === 0 ? null : heads[heads.length - 1]
    const document = JSON.stringify({ ...content, format: NOTE_FORMAT })
    this.markPending(uuid)
    this.writeNote(uuid, EnumNoteEvent.CONTENT_CHANGED, async () => ({ content: await this.encrypt(uuid, EnumNotePart.CONTENT, document), base }))
  }

  remove(uuid: string): void {
    this.writeNote(uuid, EnumNoteEvent.REMOVED, async () => ({}))
    this.markPending(uuid)
    if (this.store.openTabs.includes(uuid)) {
      this.close(uuid)
    }
  }

  restore(uuid: string): void {
    this.writeNote(uuid, EnumNoteEvent.RESTORED, async () => ({}))
    this.markPending(uuid)
  }

  resolveConflict(uuid: string, content: IDocument): void {
    this.requireUserKey()
    const note = this.supportedNote(uuid)
    this.checkLimits(content)
    const bases = note.getHeads()
    const document = JSON.stringify({ ...content, format: NOTE_FORMAT })
    this.markPending(uuid)
    this.writeNote(uuid, EnumNoteEvent.CONFLICT_RESOLVED, async () => ({ content: await this.encrypt(uuid, EnumNotePart.CONTENT, document), bases }))
  }

  isFormatSupported(uuid: string): boolean {
    const note = this.store.notes[uuid]
    return note === undefined || note.getFormat() <= NOTE_FORMAT
  }

  getNote(uuid: string): INote | null {
    const note = this.store.notes[uuid]
    if (!this.hasUserKey() || this.isHidden(uuid)) {
      return null
    }
    if (note === undefined) {
      if (this.store.previews.some((preview) => preview.getUuid() === uuid)) {
        this.refreshNote(uuid)
      }
      return null
    }
    return note
  }

  getPreviews(query: string, removed: boolean): INotePreview[] {
    const needle = query.trim().toLowerCase()
    if (!this.hasUserKey()) {
      return []
    }
    return this.store.previews
      .filter((preview) => preview.isRemoved() === removed)
      .filter((preview) => needle === '' || preview.getTitle().toLowerCase().includes(needle))
      .sort((a, b) => compareTime(b.getUpdatedAt(), a.getUpdatedAt()))
  }

  getOpenTabs(): string[] {
    const open = this.store.openTabs.filter((uuid) => this.store.notes[uuid]?.isRemoved() !== true)
    if (!this.hasUserKey()) {
      return []
    }
    return open.filter((uuid) => !this.isHidden(uuid))
  }

  getActiveTab(): string | null {
    const active = this.store.activeTab
    return active !== null && this.getOpenTabs().includes(active) ? active : null
  }

  isPending(uuid: string): boolean {
    return this.store.pendingTabs.includes(uuid)
  }

  isOpenDialogShown(): boolean {
    return this.store.isOpenDialogShown
  }

  showOpenDialog(shown: boolean): void {
    this.store.isOpenDialogShown = shown
  }

  canUseFiles(): boolean {
    return this.hasUserKey() && (typeof navigator === 'undefined' || navigator.onLine)
  }

  getFiles(uuid: string): INoteFile[] {
    if (!this.hasUserKey()) {
      return []
    }
    return this.store.files[uuid] ?? []
  }

  uploadFiles(uuid: string, entries: IUploadEntry[]): void {
    const userKey = this.requireUserKey()
    entries.forEach((entry) => {
      const transfer = this.addTransfer(EnumTransferKind.UPLOAD, entry.file.name, entry.file.size)
      this.queue.add(transfer, (running) => this.upload(uuid, entry, running, userKey))
    })
  }

  async downloadFile(uuid: string, file: string): Promise<void> {
    const record = this.fileRecord(uuid, file)
    const info = this.fileInfo.get(file)
    this.requireUserKey()
    const sink = await this.openSink(info?.getName() ?? file, record.size)
    const transfer = this.addTransfer(EnumTransferKind.DOWNLOAD, info?.getName() ?? file, record.size)
    this.queue.add(transfer, async (running) => {
      await this.withSink(sink, async () => {
        const reader = this.chunkReader(record, await this.fileKey(record), running)
        const count = chunkCount(record.size, record.chunk)
        for (let index = 0; index < count; index++) {
          this.throwIfCancelled(running)
          const plain = await reader.read(index)
          await sink.write(index * record.chunk, plain)
          running._setDone(index * record.chunk + plain.length)
        }
      })
    })
  }

  async downloadFolder(uuid: string, path: string): Promise<void> {
    const prefix = path + '/'
    const files = this.getFiles(uuid)
      .filter((file) => file.isReadable() && file.getPath().startsWith(prefix))
      .sort((a, b) => a.getPath().localeCompare(b.getPath()))
    if (files.length === 0) {
      throw new Error(EnumNoteError.NOTE_NOT_FOUND)
    }
    this.requireUserKey()
    const name = (path.split('/').pop() ?? path) + '.zip'
    const entries = files.map((file) => ({ path: file.getPath().slice(prefix.length), size: file.getSize() }))
    const sink = await this.openSink(name, ZipWriter.archiveSize(entries))
    const transfer = this.addTransfer(EnumTransferKind.DOWNLOAD, name, entries.reduce((sum, entry) => sum + entry.size, 0))
    this.queue.add(transfer, async (running) => {
      await this.withSink(sink, async () => {
        const zip = new ZipWriter(sink)
        let done = 0
        for (const [index, file] of files.entries()) {
          const record = this.fileRecord(uuid, file.getUuid())
          const reader = this.chunkReader(record, await this.fileKey(record), running)
          await zip.addFile(entries[index].path, record.size, async (offset) => {
            this.throwIfCancelled(running)
            const plain = await reader.read(Math.floor(offset / record.chunk))
            running._setDone(done + offset + plain.length)
            return plain
          })
          done += record.size
        }
        await zip.close()
      })
    })
  }

  async removeFiles(uuid: string, files: string[]): Promise<void> {
    this.requireUserKey()
    const response = await new RemoveFilesRoute(uuid).exec(new RemoveFilesParams({ files }))
    if (!response.isSuccess()) {
      throw new Error(fileError(response.getError()))
    }
    const removed = response.getData().removed
    if (removed.length === 0) {
      return
    }
    this.markPending(uuid)
    this.writeNote(uuid, EnumNoteEvent.FILES_REMOVED, async () => ({ files: removed }))
  }

  getTransfers(): ITransfer[] {
    return this.store.transfers
  }

  cancelTransfer(id: string): Promise<void> {
    const transfer = this.store.transfers.find((item) => item.getId() === id)
    if (transfer !== undefined) {
      this.queue.cancel(transfer as Transfer)
    }
    return Promise.resolve()
  }

  /** Передача попадает в store первой: очередь работает с её реактивной копией, чтобы прогресс был виден */
  private addTransfer(kind: EnumTransferKind, title: string, size: number): Transfer {
    this.store.transfers = [...this.store.transfers, new Transfer(uuidv4(), kind, title, size)]
    return this.store.transfers[this.store.transfers.length - 1] as Transfer
  }

  /**
   * Загрузка файла: сессия на узле, куски строго по порядку с продолжением после обрыва с
   * принятого узлом места, затем событие file_attached через цепочку записи заметки
   */
  private async upload(uuid: string, entry: IUploadEntry, transfer: Transfer, userKey: IUserKey): Promise<void> {
    const key = await this.fileCipher.createKey()
    const wrapped = await userKey.wrap(key)
    const meta = await this.fileCipher.encryptMeta(key, { name: entry.file.name, path: entry.path, mime: entry.file.type })
    const size = entry.file.size
    const chunk = transferSettings.chunkBytes
    const count = chunkCount(size, chunk)
    const cipherSize = size + GCM_TAG_BYTES * count
    const cipherChunk = chunk + GCM_TAG_BYTES
    const session = await this.repeatUntilDone(transfer, () => new CreateUploadRoute(uuid).exec(new CreateUploadParams({ size: cipherSize })))
    let received = session.received
    let file = session.file
    let attempt = 0
    try {
      while (file === null) {
        this.throwIfCancelled(transfer)
        const index = Math.floor(received / cipherChunk)
        const plain = new Uint8Array(await entry.file.slice(index * chunk, Math.min((index + 1) * chunk, size)).arrayBuffer())
        const cipher = await this.fileCipher.encryptChunk(key, index, count, plain)
        const params = new ChunkParams({ from: received, to: received + cipher.length - 1, size: cipherSize })
        const response = await new SendChunkRoute(session.upload).sendBytes(params, new Blob([cipher as BlobPart]))
        if (response.isSuccess()) {
          received = response.getData().received
          file = response.getData().file
          attempt = 0
          transfer._setDone(Math.min((index + 1) * chunk, size))
          continue
        }
        this.throwIfFatal(response.getError())
        await this.pause(transfer, attempt++)
        received = (await this.repeatUntilDone(transfer, () => new GetUploadRoute(new UploadParams({ upload: session.upload })).exec(null))).received
      }
    } catch (e) {
      await this.cancelUpload(e, session.upload)
      throw e
    }
    const attached = file
    this.markPending(uuid)
    this.writeNote(uuid, EnumNoteEvent.FILE_ATTACHED, async () => ({ file: attached, size, chunk, key: wrapped, meta }))
  }

  /** Повторять запрос с паузой, пока он не удастся; ошибка права или размера — отказ передачи */
  private async repeatUntilDone<T>(transfer: Transfer, request: () => Promise<ApiResponse<T>>): Promise<T> {
    for (let attempt = 0; ; attempt++) {
      this.throwIfCancelled(transfer)
      const response = await request()
      if (response.isSuccess()) {
        return response.getData()
      }
      this.throwIfFatal(response.getError())
      await this.pause(transfer, attempt)
    }
  }

  private throwIfFatal(code: string): void {
    if (!isRetryable(code)) {
      throw new TransferError(fileError(code))
    }
  }

  /** Пауза 2, 4, 8 … до 60 с; отмена во время паузы прерывает передачу */
  private async pause(transfer: Transfer, attempt: number): Promise<void> {
    const ms = Math.min(transferSettings.retryBaseMs * 2 ** attempt, transferSettings.retryMaxMs)
    await new Promise((resolve) => setTimeout(resolve, ms))
    this.throwIfCancelled(transfer)
  }

  private throwIfCancelled(transfer: Transfer): void {
    if (transfer.isCancelRequested()) {
      throw new TransferCancelled()
    }
  }

  /** Отмена загрузки пользователем: принятые куски стираются с узла; сбой самой отмены не важен */
  private async cancelUpload(error: unknown, upload: string): Promise<void> {
    if (!(error instanceof TransferCancelled)) {
      return
    }
    await new CancelUploadRoute(new UploadParams({ upload })).exec(null).catch(() => undefined)
  }

  private fileRecord(uuid: string, file: string): INoteFileRecord {
    const record = this.store.notes[uuid]?.getFiles().find((item) => item.file === file)
    if (record === undefined) {
      throw new Error(EnumNoteError.NOTE_NOT_FOUND)
    }
    return record
  }

  private async fileKey(record: INoteFileRecord): Promise<CryptoKey> {
    const cached = this.fileKeys.get(record.file)
    if (cached !== undefined) {
      return cached
    }
    const key = await this.requireUserKey().unwrap(record.key)
    this.fileKeys.set(record.file, key)
    return key
  }

  private async openSink(name: string, size: number): Promise<IFileSink> {
    const saver = FileSaverProviderModule()
    if (!saver.isSupported()) {
      throw new Error(EnumNoteFileError.SAVE_NOT_SUPPORTED)
    }
    return saver.open(name, size)
  }

  /** Файл на диске закрывается после успеха и бросается при любой ошибке или отмене */
  private async withSink(sink: IFileSink, write: () => Promise<void>): Promise<void> {
    try {
      await write()
      await sink.close()
    } catch (e) {
      await sink.abort().catch(() => undefined)
      throw e
    }
  }

  /** Куски файла с узла с опережением; неудачный кусок повторяется до предела, нет на узле — NOT_ON_NODE */
  private chunkReader(record: INoteFileRecord, key: CryptoKey, transfer: Transfer): ChunkReader {
    const count = chunkCount(record.size, record.chunk)
    const cipherChunk = record.chunk + GCM_TAG_BYTES
    const cipherSize = record.size + GCM_TAG_BYTES * count
    return new ChunkReader(count, async (index) => {
      const params = new FileChunkParams({ from: index * cipherChunk, to: Math.min((index + 1) * cipherChunk, cipherSize) - 1 })
      for (let attempt = 0; ; attempt++) {
        this.throwIfCancelled(transfer)
        const response = await new GetFileChunkRoute(record.file).getBytes(params)
        if (response.isSuccess()) {
          return this.fileCipher.decryptChunk(key, index, count, new Uint8Array(response.getData()))
        }
        this.throwIfFatal(response.getError())
        if (attempt >= transferSettings.downloadRetries) {
          throw new TransferError(fileError(response.getError()))
        }
        await this.pause(transfer, attempt)
      }
    })
  }

  /** Сведения файлов заметки: ключ файла и сведения расшифровываются один раз на файл */
  private async refreshFiles(uuid: string, records: INoteFileRecord[]): Promise<void> {
    const files = await Promise.all(records.map((record) => this.noteFile(record)))
    this.store.files = { ...this.store.files, [uuid]: files }
  }

  private async noteFile(record: INoteFileRecord): Promise<INoteFile> {
    const cached = this.fileInfo.get(record.file)
    if (cached !== undefined) {
      return cached
    }
    let file: INoteFile
    try {
      const meta = await this.fileCipher.decryptMeta(await this.fileKey(record), record.meta)
      file = new NoteFile(record.file, meta.name, meta.path, record.size, meta.mime, true)
    } catch (e) {
      if (!this.isDecryptError(e)) {
        throw e
      }
      file = NoteFile.unreadable(record.file, record.size)
    }
    this.fileInfo.set(record.file, file)
    return file
  }

  private stop(): void {
    if (this.pendingTimer !== null) {
      clearInterval(this.pendingTimer)
      this.pendingTimer = null
    }
  }

  private reset(): void {
    this.stop()
    this.starting = null
    this.store.notes = {}
    this.store.previews = []
    this.store.openTabs = []
    this.store.activeTab = null
    this.store.pendingTabs = []
    this.store.files = {}
    this.store.transfers = []
    this.keys.clear()
    this.fileKeys.clear()
    this.fileInfo.clear()
    this.unreadable.clear()
    this.legacy.clear()
    this.writing.clear()
    this.refreshing.clear()
  }

  private async load(userUuid: string): Promise<void> {
    const rows = await readNoteIndex(userUuid)
    this.store.previews = rows.map((row) => new NotePreview(row.uuid, row.title, row.updatedAt, row.isRemoved))
    await Promise.all(rows.map((row) => this.hideIfLegacy(row.uuid)))
    await this.refreshTabs()
    await Promise.all(this.store.openTabs.map((uuid) => this.refreshNote(uuid)))
  }

  private onNoteOperation(operation: IOperation): void {
    this.refreshNote(operation.record)
  }

  private onTabsOperation(operation: IOperation): void {
    if (operation.record === this.userUuid) {
      this.refreshTabs()
    }
  }

  private async refreshNote(uuid: string): Promise<void> {
    const number = (this.refreshing.get(uuid) ?? 0) + 1
    this.refreshing.set(uuid, number)
    const operations = await sys().sync.getRecordOperations(EnumNoteSet.NOTES, uuid)
    if (operations.length === 0 || !this.hasUserKey()) {
      return
    }
    const state = this.foldNote.exec(operations)
    if (state.key === null) {
      await this.hideLegacy(uuid)
      return
    }
    let row: INoteIndexRow
    try {
      const plain = await this.decryptNote.exec(state, await this.noteKey(uuid, state.key))
      if (this.refreshing.get(uuid) !== number) {
        return
      }
      const note = new Note(uuid, plain)
      this.unreadable.delete(uuid)
      this.store.notes[uuid] = note
      row = { uuid, title: note.getTitle(), updatedAt: note.getUpdatedAt(), isRemoved: note.isRemoved() }
      await this.refreshFiles(uuid, plain.files)
    } catch (e) {
      if (!this.isDecryptError(e)) {
        throw e
      }
      if (this.refreshing.get(uuid) !== number) {
        return
      }
      this.unreadable.add(uuid)
      delete this.store.notes[uuid]
      row = { uuid, title: UNREADABLE_TITLE, updatedAt: state.updatedAt, isRemoved: state.isRemoved }
    }
    this.setPreview(row)
    await writeNoteIndexRow(this.userUuid, row)
  }

  private setPreview(row: INoteIndexRow): void {
    this.store.previews = [
      ...this.store.previews.filter((preview) => preview.getUuid() !== row.uuid),
      new NotePreview(row.uuid, row.title, row.updatedAt, row.isRemoved),
    ]
  }

  /** Строка индекса от прошлой версии клиента может принадлежать устаревшей заметке */
  private async hideIfLegacy(uuid: string): Promise<void> {
    const operations = await sys().sync.getRecordOperations(EnumNoteSet.NOTES, uuid)
    if (operations.length > 0 && this.foldNote.exec(operations).key === null) {
      await this.hideLegacy(uuid)
    }
  }

  private async hideLegacy(uuid: string): Promise<void> {
    this.legacy.add(uuid)
    delete this.store.notes[uuid]
    this.store.previews = this.store.previews.filter((preview) => preview.getUuid() !== uuid)
    await removeNoteIndexRow(this.userUuid, uuid)
  }

  private isHidden(uuid: string): boolean {
    return this.unreadable.has(uuid) || this.legacy.has(uuid)
  }

  /** Чужой ключ или испорченный шифротекст: заметка остаётся в списке, но не открывается */
  private isDecryptError(error: unknown): boolean {
    return error instanceof Error && (error.message === EnumNoteError.CANNOT_DECRYPT || error.message === EnumUserKeyError.WRONG_KEY)
  }

  /** Ключ заметки из памяти модуля; иначе unwrap обёрнутого ключа из события created */
  private async noteKey(uuid: string, wrapped?: string): Promise<CryptoKey> {
    const cached = this.keys.get(uuid)
    if (cached !== undefined) {
      return cached
    }
    const key = wrapped ?? this.foldNote.exec(await sys().sync.getRecordOperations(EnumNoteSet.NOTES, uuid)).key
    if (key === null) {
      throw new Error(EnumNoteError.NOTE_NOT_FOUND)
    }
    const unwrapped = await this.requireUserKey().unwrap(key)
    this.keys.set(uuid, unwrapped)
    return unwrapped
  }

  private async encrypt(uuid: string, part: EnumNotePart, text: string): Promise<string> {
    return this.cipher.encrypt(await this.noteKey(uuid), part, text)
  }

  private requireUserKey(): IUserKey {
    const userKey = sys().auth.getUserKey()
    if (userKey === null) {
      throw new Error(EnumNoteError.NO_USER_KEY)
    }
    return userKey
  }

  private async refreshTabs(): Promise<void> {
    const operations = await sys().sync.getRecordOperations(EnumNoteSet.TABS, this.userUuid)
    const tabs = this.foldTabs.exec(operations)
    this.store.openTabs = tabs.open
    this.store.activeTab = tabs.active
    await Promise.all(tabs.open.filter((uuid) => this.store.notes[uuid] === undefined).map((uuid) => this.refreshNote(uuid)))
  }

  private writeTabs(open: string[], active: string | null): void {
    this.store.openTabs = open
    this.store.activeTab = active
    this.write(EnumNoteSet.TABS, this.userUuid, EnumTabsEvent.TABS_CHANGED, { open, active })
  }

  /** Событие заметки после всех её событий, вызванных раньше; ошибка — в консоль, события нет */
  private writeNote(uuid: string, event: EnumNoteEvent, fields: () => Promise<Record<string, unknown>>): void {
    this.writesInFlight++
    const next = (this.writing.get(uuid) ?? Promise.resolve())
      .then(async () => this.write(EnumNoteSet.NOTES, uuid, event, await fields()))
      .catch((e) => SysConsoleProviderModule().log(`Note ${uuid}: event ${event} is not written: ${e}`, EnumConsoleType.ERROR))
      .finally(() => { this.writesInFlight-- })
    this.writing.set(uuid, next)
  }

  private write(set: EnumNoteSet, record: string, event: string, fields: Record<string, unknown>): IOperation {
    return sys().sync.write({ set, record, kind: EnumOperationKind.EVENT, event, fields })
  }

  private markPending(uuid: string): void {
    if (!this.store.pendingTabs.includes(uuid)) {
      this.store.pendingTabs = [...this.store.pendingTabs, uuid]
    }
  }

  private checkPending(): void {
    if (this.store.pendingTabs.length > 0 && this.writesInFlight === 0 && sys().sync.getQueueSize() === 0) {
      this.store.pendingTabs = []
    }
  }

  /** Заметка, содержимое которой клиент может править; иначе Error с кодом EnumNoteError */
  private supportedNote(uuid: string): INote {
    const note = this.store.notes[uuid]
    if (note === undefined) {
      throw new Error(EnumNoteError.NOTE_NOT_FOUND)
    }
    if (!this.isFormatSupported(uuid)) {
      throw new Error(EnumNoteError.FORMAT_NOT_SUPPORTED)
    }
    return note
  }

  private checkLimits(content: IDocument): void {
    if (this.hasImageOverLimit(content.content)) {
      throw new Error(EnumNoteError.IMAGE_TOO_LARGE)
    }
    if (new TextEncoder().encode(JSON.stringify(content)).length > NOTE_LIMIT_BYTES) {
      throw new Error(EnumNoteError.NOTE_TOO_LARGE)
    }
  }

  private hasImageOverLimit(nodes: IDocumentNode[]): boolean {
    return nodes.some((node) => {
      const src = node.type === 'image' ? node.attrs?.src : undefined
      if (typeof src === 'string' && imageBytes(src) > IMAGE_LIMIT_BYTES) {
        return true
      }
      return this.hasImageOverLimit(node.content ?? [])
    })
  }

  private nextUntitled(): string {
    const used = new Set<number>()
    this.store.previews
      .filter((preview) => !preview.isRemoved())
      .forEach((preview) => {
        const match = /^Без названия (\d+)$/.exec(preview.getTitle())
        if (match !== null) {
          used.add(Number(match[1]))
        }
      })
    let number = 1
    while (used.has(number)) {
      number++
    }
    return `${UNTITLED} ${number}`
  }
}

export default function (): NoteModuleV1 {
  return NoteModuleV1.getInstance()
}
